import { useMemo } from 'react';

import type { ContributorsMacroRendererProps } from '../ContributorsMacroRenderer.tsx';

import type { IncludeType, OrderType } from './useProcessedContributorsResult.ts';

type MacroParams = ContributorsMacroRendererProps['macroParams'];

type LayoutType = 'list' | 'inline';

export interface ParsedMacroParams {
	include: IncludeType[];
	order: OrderType;
	reverse: boolean;
	limit: number;
	mode: LayoutType;
	showAnonymous: boolean;
	showCount: boolean;
	showTime: boolean;
	noneFoundMessage?: string;
	macroParamsForQuery: {
		include?: string;
		order?: string;
		reverse: boolean;
		showAnonymous: boolean;
		page?: string;
		labels?: string;
		label?: string;
		spaces?: string;
		space?: string;
		contentType?: string;
		publishDate?: string;
		scope?: string;
	};
}

export const parseInclude = (value?: string): IncludeType[] => {
	if (!value) {
		return ['authors'];
	}
	return value.split(',').map((s) => s.trim()) as IncludeType[];
};

export const parseOrder = (value?: string): OrderType => {
	const validOrders: OrderType[] = ['count', 'name', 'update', 'edits', 'editTime'];
	if (value && validOrders.includes(value as OrderType)) {
		return value as OrderType;
	}
	return 'count';
};

export const parseBoolean = (value?: string): boolean => {
	return value === 'true';
};

export const parseLimit = (value?: string): number => {
	if (!value) {
		return -1;
	}
	const parsed = parseInt(value, 10);
	return isNaN(parsed) ? -1 : Math.max(parsed, -1);
};

export const composeMacroParamsForQuery = (
	macroParams: MacroParams,
): ParsedMacroParams['macroParamsForQuery'] => {
	const reverse = parseBoolean(macroParams?.reverse?.value);
	const showAnonymous = parseBoolean(macroParams?.showAnonymous?.value);

	return {
		include: macroParams?.include?.value,
		order: macroParams?.order?.value,
		reverse,
		showAnonymous,
		page: macroParams?.page?.value,
		labels: macroParams?.labels?.value,
		label: macroParams?.label?.value,
		spaces: macroParams?.spaces?.value,
		space: macroParams?.space?.value,
		contentType: macroParams?.contentType?.value,
		publishDate: macroParams?.publishDate?.value,
		scope: macroParams?.scope?.value,
	};
};

export const useParsedMacroParams = (macroParams: MacroParams): ParsedMacroParams => {
	return useMemo(() => {
		const include = parseInclude(macroParams?.include?.value);
		const order = parseOrder(macroParams?.order?.value);
		const reverse = parseBoolean(macroParams?.reverse?.value);
		const limit = parseLimit(macroParams?.limit?.value);
		const mode: LayoutType = macroParams?.mode?.value === 'list' ? 'list' : 'inline';
		const showAnonymous = parseBoolean(macroParams?.showAnonymous?.value);
		const showCount = parseBoolean(macroParams?.showCount?.value);
		const showTime = parseBoolean(macroParams?.showLastTime?.value);
		const noneFoundMessage = macroParams?.noneFoundMessage?.value;
		const macroParamsForQuery = composeMacroParamsForQuery(macroParams);

		return {
			include,
			order,
			reverse,
			limit,
			mode,
			showAnonymous,
			showCount,
			showTime,
			noneFoundMessage,
			macroParamsForQuery,
		};
	}, [macroParams]);
};
